import {CONFIG} from '../../appConfig';
import content from './content';
import contract from './contract';
import coupon from './coupon';
import location from './location';
import profile from './profile';
import reference from './reference';
import reservations from './reservations';
import session from './session';
import trips from './trips';
import rewards from './rewards';
import vehicles from './vehicles';
import ticket from './ticket';
import prep from './prep';
import subscriptions from './subscriptions';
import mobileWallet from './mobileWallet';
import enrollment from './enrollment';
import rentalTicket from './rentalTicket';
import {types} from '../../utils/js/types';
import {getObjFromStrPairs} from '../../utils/js/object';
import dynamicContent from "./dynamicContent";

/**
 * @namespace "services.gma"
 */
export default {
  /**
   * @namespace "services.gma.content"
   */
  content,
  /**
   * @namespace "services.gma.contract"
   */
  contract,
  /**
   * @namespace "services.gma.coupon"
   */
  coupon,
  /**
   * @desc Pass in a brand and retrieve the object of possible domain and country mappings for that brand
   * @param {Object} data {endpoint, opts}
   * @returns {Object}
   * @memberof services.gma
   * @example
   * const endpoint = {brand:'enterprise'};
   * const opts = {legacy:'true'}; // defaults to false
   * services.gma.countryToDomain({endpoint});
   * // NOTE: we do not save the resp in the store as calling this service should result in an cross-app redirect
   * // NOTE: if no brand is provided, service will retrieve domain mappings based on CONFIG.BRAND setting
   * // NOTE: if opts.legacy is true, uses ${brand}/deeplinks endpoint, default/false is countryToDomain/${brand}
   */
  countryToDomain(data = {}) {
    const {endpoint = {}, opts = {}} = data;
    const {brand = CONFIG.BRAND.toLowerCase()} = endpoint;
    const {legacy = false} = opts;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: legacy ? `${brand.toLowerCase()}/deeplinks` : `countryToDomain/${brand.toLowerCase()}`
    };
  },
  /**
   * @desc Pass in object (or string) of query params to get a redirect response and updates GMA session
   * @param {Object|String} data {params, endpoint}
   * @returns {Object}
   * @memberof services.gma
   * @example
   * // pass in a query string...
   * const params = '?pickUpLocation.searchCriteria=1018963&dropOffLocation.searchCriteria=1018963&pickUpDateTime.date=8/14/2018&dropOffDateTime.date=8/17/2018&pickUpDateTime.time=12:00&dropOffDateTime.time=12:00&age=30&oneway=false&prepay_selected=false';
   * services.gma.deeplink({params});
   *
   * // ...or an object of params
   * const data = {
   *  params: {
   *    "pickUpLocation.searchCriteria":"1018963",
   *    "dropOffLocation.searchCriteria":"1018963",
   *    "pickUpDateTime.date":"8/14/2018",
   *    "dropOffDateTime.date":"8/17/2018",
   *    "pickUpDateTime.time":"12:00",
   *    "dropOffDateTime.time":"12:00",
   *    "age":"30",
   *    "oneway":"false",
   *    "prepay_selected":"false"
   *  },
   *  // optional endpoint version declaration, defaults to "2"
   *  endpoint: {
   *    version: "2"
   *  }
   * }
   * services.gma.deeplink(data);
   *
   * // service will update redis session and return updated session
   */
  deeplink(data = {}) {
    const {endpoint = {}, params = {}} = data;
    const {version = '2'} = endpoint;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `deeplink/v${version}`,
      params: (types(params).isString ? getObjFromStrPairs(params) : params)
    };
  },
  /**
   * @namespace "services.gma.location"
   */
  location,
  /**
   * @namespace "services.gma.profile"
   */
  profile,
  /**
   * @namespace "services.gma.reference"
   */
  reference,
  /**
   * @namespace "services.gma.reservations"
   */
  reservations,
  /**
   * @namespace "services.gma.session"
   */
  session,
  /**
   * @namespace "services.gma.trips"
   */
  trips,
  /**
   * @namespace "services.gma.rewards"
   */
  rewards,
  /**
   * @namespace "services.gma.vehicles"
   */
  vehicles,
  /**
   * @namespace "services.gma.ticket"
   */
  ticket,
  /**
   * @namespace "services.gma.prep"
   */
  prep,
  /**
   * @namespace "services.gma.subscriptions"
   */
  subscriptions,
  /**
   * @namespace "services.gma.mobileWallet"
   */
  mobileWallet,
  /**
   * @namespace "services.gma.enrollment"
   */
  enrollment,
  /**
   * @namespace "services.gma.rentalTicket"
   */
  rentalTicket,
  /**
   * @namespace "services.gma.dynamicContent"
   */
  dynamicContent,
};
